import { Search, X, Loader2 } from "lucide-react";

interface KnowledgeNode {
	nid: string;
	name: string;
	properties?: string;
}

interface SearchModalProps {
	open: boolean;
	onClose: () => void;
	searchQuery: string;
	onQueryChange: (query: string) => void;
	onSearch: () => void;
	results: KnowledgeNode[];
	loading: boolean;
	searched: boolean;
	onFocusNode: (nid: string) => void;
}

export default function SearchModal({
	open,
	onClose,
	searchQuery,
	onQueryChange,
	onSearch,
	results,
	loading,
	searched,
	onFocusNode,
}: SearchModalProps) {
	if (!open) return null;

	return (
		<div
			className="absolute inset-0 z-50 flex items-start justify-center pt-16"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div className="w-full max-w-lg mx-4 bg-slate-800/95 backdrop-blur-xl border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden">
				<div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50">
					<h3 className="text-sm font-medium text-white">Node Search</h3>
					<button
						onClick={onClose}
						className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-all"
						title="Close search"
					>
						<X className="w-4 h-4" />
					</button>
				</div>
				<div className="p-4">
					<div className="flex gap-2">
						<div className="relative flex-1">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => onQueryChange(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && onSearch()}
								placeholder="Search nodes by name..."
								autoFocus
								className="w-full pl-10 pr-3 py-2.5 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-sm transition-all"
							/>
						</div>
						<button
							onClick={onSearch}
							disabled={loading}
							className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-xl text-sm font-medium transition-all"
						>
							{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
						</button>
					</div>
					<div className="mt-3 max-h-64 overflow-y-auto space-y-1.5">
						{searched && !loading && results.length === 0 && (
							<p className="text-slate-500 text-center py-6 text-sm">No nodes found</p>
						)}
						{results.map((node) => (
							<button
								key={node.nid}
								onClick={() => onFocusNode(node.nid)}
								className="w-full text-left px-3 py-2 rounded-lg bg-slate-900/30 hover:bg-slate-700/50 border border-transparent hover:border-slate-600/50 transition-all group"
							>
								<span className="text-sm text-white group-hover:text-blue-400 transition-colors">{node.name}</span>
								<span className="text-xs text-slate-500 ml-2 font-mono">{node.nid.slice(0, 8)}...</span>
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
